import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { InboxItem } from '../../../OperationsInbox/types';
import { AutocompleteAircraft } from '../../../Settings/HangarSettings/AutocompleteAircraft';

type Props = {
  item: InboxItem;
  onResolve: (pendingTenantId: string, aircraftId: string) => Promise<void>;
  isProcessing: boolean;
};

/**
 * Simplified quarantine item card for the Ops queue
 * Shows unknown aircraft that need to be resolved/mapped
 */
export const QuarantineItem: React.FC<Props> = ({ item, onResolve, isProcessing }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showSearch, setShowSearch] = React.useState(false);
  const [isCompleting, setIsCompleting] = React.useState(false);

  const handleAircraftSelect = async (aircraft: { id: string; make: string; model: string } | null) => {
    if (!aircraft) return;

    try {
      setIsCompleting(true);
      await onResolve(item.item_id, aircraft.id);
    } catch (err) {
      setIsCompleting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderLeft: '3px solid',
        borderLeftColor: theme.palette.error.main,
        bgcolor: isCompleting ? theme.palette.success.light + '10' : theme.palette.background.paper,
        opacity: isCompleting ? 0.7 : 1,
        transition: 'all 150ms ease-out',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {/* Tail Number */}
        <Chip
          label={item.tail_number}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontWeight: 700, fontSize: 12 }}
        />

        {/* Type Hint */}
        {item.aircraft_type_hint && (
          <Typography
            variant="body2"
            sx={{
              fontSize: 12,
              color: theme.palette.primary.main,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 120,
            }}
            title={item.aircraft_type_hint}
          >
            {item.aircraft_type_hint}
          </Typography>
        )}

        {/* Operator */}
        {item.operator_name && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 11, flex: 1 }}
          >
            {item.operator_name}
          </Typography>
        )}

        {/* Completion indicator */}
        {isCompleting && (
          <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
        )}

        {/* Resolve Button / Search */}
        {!isCompleting && (
          <>
            {!showSearch ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={() => setShowSearch(true)}
                disabled={isProcessing}
                sx={{
                  ml: 'auto',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.25,
                  fontSize: 11,
                  textTransform: 'none',
                }}
              >
                {t('screens.ops.queue.actions.resolve')}
              </Button>
            ) : (
              <ClickAwayListener onClickAway={() => setShowSearch(false)}>
                <Box sx={{ width: 180, ml: 'auto' }}>
                  <AutocompleteAircraft
                    label={t('screens.ops.queue.actions.searchAircraft', 'Search aircraft')}
                    defaultSelected={null}
                    onSelect={handleAircraftSelect}
                    initialSearchText={item.aircraft_type_hint || ''}
                    autoOpen={true}
                    onDismiss={() => setShowSearch(false)}
                    disablePortal={true}
                  />
                </Box>
              </ClickAwayListener>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};
